import { useState, useRef, useEffect } from "react";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";
import PrizePicker from "../PrizePicker.jsx";

/* ==================== ANDEDAMMEN – magnetfiskeri ====================
   En realistisk andedam med 24 gummiænder i drift. Spilleren fisker 10
   ænder op med en magnetfiskestang (magneten griber pladen på andens RYG);
   mærket i BUNDEN afsløres først, når anden er hevet op og vendes:
     grønt mærke  -> tæller mod LILLE bamse
     blåt mærke   -> tæller mod MELLEM bamse
     guldmønt     -> tæller mod STOR bamse
     joker        -> tæller ikke
   3 ENS mærker = 1 bamse af den størrelse (6 ens = 2, osv.). Mærkerne er
   en ÆGTE pulje fordelt i dammen (8 grøn / 5 blå / 3 guld / 8 joker), så
   oddsene er hypergeometriske — dammen "har" fysisk mærkerne.
==================================================================== */

const W = 360, H = 540;
const POND = { x: 22, y: 96, w: 316, h: 318, r: 64 };   // vandspejlet
const PC = { x: POND.x + POND.w / 2, y: POND.y + POND.h / 2 };
/* flettet kurv m. 2 rækker x 5 reder til de fiskede ænder (Gemini-mockup) */
const BASKET = { x: 18, y: 424, w: 324, h: 112 };
const slotPos = (i) => ({
  x: BASKET.x + 40 + (i % 5) * 61,
  y: BASKET.y + 34 + Math.floor(i / 5) * 46,
});
const N_DUCKS = 24;
const CATCHES = 10;
const GRAB_R = 30;                        // magnetens fangstradius
const DECK = { gron: 8, blaa: 5, guld: 3, joker: 8 };   // mærke-puljen
const MARK_COLOR = { gron: "#3fae46", blaa: "#2f7fd4", guld: "#e8c15a", joker: "#9b59b6" };
const MARK_SIZE = { gron: "lille", blaa: "mellem", guld: "stor" };

/* ---- lyd (fælles context via audio.js) ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    splash() {
      const c = ensure(); if (!c) return;
      const o = c.createOscillator(), g = c.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(420, c.currentTime);
      o.frequency.exponentialRampToValueAtTime(90, c.currentTime + 0.22);
      g.gain.setValueAtTime(0.14, c.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.26);
      o.connect(g); g.connect(out()); o.start(); o.stop(c.currentTime + 0.28);
    },
    snap() { const c = ensure(); if (c) tone(980, c.currentTime, 0.08, "square", 0.10); },
    reel() {
      const c = ensure(); if (!c) return;
      for (let i = 0; i < 4; i++) tone(1400 + i * 60, c.currentTime + i * 0.055, 0.03, "square", 0.05);
    },
    reveal(kind) {
      const c = ensure(); if (!c) return;
      if (kind === "joker") { tone(220, c.currentTime, 0.3, "sawtooth", 0.1); tone(160, c.currentTime + 0.13, 0.35, "sawtooth", 0.1); }
      else if (kind === "guld") [660, 880, 1100, 1320].forEach((f, i) => tone(f, c.currentTime + i * 0.09, 0.22, "triangle", 0.12));
      else [520 + (kind === "blaa" ? 140 : 0), 780].forEach((f, i) => tone(f, c.currentTime + i * 0.1, 0.2, "triangle", 0.11));
    },
    fanfare() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046, 1318].forEach((f, i) => tone(f, c.currentTime + i * 0.11, 0.28, "triangle", 0.13));
    },
  };
}

const shuffle = (a) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function DuckPond({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | result
  const [result, setResult] = useState(null);    // {counts, prizes:[sizes]}
  const [pickQueue, setPickQueue] = useState(null); // null | ["stor","lille",...]
  const [hud, setHud] = useState({ fished: 0, marks: [] });
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setResult(null);
    setPickQueue(null);
    setHud({ fished: 0, marks: [] });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    /* mærkerne fordeles fysisk i dammen */
    const deck = shuffle(Object.entries(DECK).flatMap(([k, n]) => Array(n).fill(k)));

    const g = {
      t: 0,
      ducks: [],
      aim: { x: PC.x, y: PC.y },
      state: "idle",            // idle | lower | lift | reveal | rack
      stT: 0,                   // tid i nuværende state
      target: null,             // index på fanget and
      caught: [],               // [{mark}] i rack-rækkefølge
      ripples: [],
      drops: [],                // vanddråber under løft
      popup: null,              // {txt, color, t}
      rippleT: 0,
      over: false,
    };
    /* 24 ænder i "baner" (ellipser om centrum) m. individuel fart/fase */
    for (let i = 0; i < N_DUCKS; i++) {
      const ring = i % 4;
      const a = 42 + ring * 30 + (i % 3) * 7;          // halvakse x
      const b = 30 + ring * 26 + ((i + 1) % 3) * 6;    // halvakse y
      g.ducks.push({
        a: Math.min(a, POND.w / 2 - 30),
        b: Math.min(b, POND.h / 2 - 30),
        th: (i / N_DUCKS) * Math.PI * 2 + ring * 0.7,
        sp: (0.10 + (i % 5) * 0.03) * (ring % 2 ? 1 : -1),
        bob: Math.random() * Math.PI * 2,
        mark: deck[i],
        state: "swim",          // swim | hooked | racked
      });
    }
    if (localStorage.getItem("carnival.debug")) {
      window.__dp = g;
      g.duckPos = (i) => duckPos(g.ducks[i]);
      g.instantCatch = (i) => { if (g.state === "idle" && g.ducks[i].state === "swim") beginCatch(duckPos(g.ducks[i]), i); };
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    const duckPos = (d) => ({
      x: PC.x + Math.cos(d.th) * d.a,
      y: PC.y + Math.sin(d.th) * d.b + Math.sin(g.t * 2 + d.bob) * 1.6,
    });

    /* ---- statisk baggrund: trædæk, sten, siv, åkander ---- */
    const bg = document.createElement("canvas");
    bg.width = W * 2; bg.height = H * 2;
    {
      const b = bg.getContext("2d");
      b.scale(2, 2);
      const rnd = (i) => { const v = Math.sin(i * 127.1 + 311.7) * 43758.5453; return v - Math.floor(v); };
      // trædæk-planker
      for (let i = 0; i < 14; i++) {
        const y = i * 40 - 8;
        const tone = 0.85 + rnd(i) * 0.3;
        b.fillStyle = `rgb(${Math.round(122 * tone)},${Math.round(82 * tone)},${Math.round(46 * tone)})`;
        b.fillRect(0, y, W, 40);
        b.fillStyle = "rgba(30,15,5,0.35)";
        b.fillRect(0, y, W, 2);
        for (let k = 0; k < 3; k++) {
          b.fillStyle = "rgba(30,15,5,0.25)";
          b.beginPath(); b.arc(30 + rnd(i * 7 + k) * 300, y + 8 + rnd(i * 13 + k) * 26, 1.6, 0, Math.PI * 2); b.fill();
        }
      }
      // dammens stenkant
      const rr2 = (x, y, w2, h2, r) => {
        b.beginPath();
        b.moveTo(x + r, y);
        b.arcTo(x + w2, y, x + w2, y + h2, r);
        b.arcTo(x + w2, y + h2, x, y + h2, r);
        b.arcTo(x, y + h2, x, y, r);
        b.arcTo(x, y, x + w2, y, r);
        b.closePath();
      };
      rr2(POND.x - 14, POND.y - 14, POND.w + 28, POND.h + 28, POND.r + 12);
      b.fillStyle = "#8f8577"; b.fill();
      // sten langs kanten
      for (let i = 0; i < 46; i++) {
        const ang = (i / 46) * Math.PI * 2;
        const ex = PC.x + Math.cos(ang) * (POND.w / 2 + 6);
        const ey = PC.y + Math.sin(ang) * (POND.h / 2 + 6);
        const s = 5 + rnd(i) * 5;
        const tone = 0.8 + rnd(i * 3) * 0.4;
        b.fillStyle = `rgb(${Math.round(150 * tone)},${Math.round(140 * tone)},${Math.round(125 * tone)})`;
        b.beginPath(); b.ellipse(ex, ey, s, s * 0.75, ang, 0, Math.PI * 2); b.fill();
        b.fillStyle = "rgba(255,255,255,0.18)";
        b.beginPath(); b.ellipse(ex - s * 0.25, ey - s * 0.3, s * 0.4, s * 0.25, ang, 0, Math.PI * 2); b.fill();
      }
      // vandet: dyb rig blå (mockup-tonen), lysere mod midten
      rr2(POND.x, POND.y, POND.w, POND.h, POND.r);
      const wg = b.createRadialGradient(PC.x - 30, PC.y - 40, 30, PC.x, PC.y, 230);
      wg.addColorStop(0, "#4a86b4");
      wg.addColorStop(0.55, "#2e5f8f");
      wg.addColorStop(1, "#1c4066");
      b.fillStyle = wg; b.fill();
      // mørke dybde-bånd (svag bølgetegning i selve vandet)
      b.save();
      rr2(POND.x, POND.y, POND.w, POND.h, POND.r);
      b.clip();
      for (let i = 0; i < 3; i++) {
        b.strokeStyle = "rgba(12,34,60,0.18)";
        b.lineWidth = 9;
        b.beginPath();
        for (let x = POND.x; x <= POND.x + POND.w; x += 10) {
          const y2 = POND.y + 84 + i * 92 + Math.sin(x * 0.045 + i * 2.4) * 7;
          x === POND.x ? b.moveTo(x, y2) : b.lineTo(x, y2);
        }
        b.stroke();
      }
      b.restore();
      b.lineWidth = 1;
      // himmel-refleks
      rr2(POND.x, POND.y, POND.w, POND.h, POND.r);
      const sk = b.createLinearGradient(0, POND.y, 0, POND.y + POND.h);
      sk.addColorStop(0, "rgba(200,230,245,0.20)");
      sk.addColorStop(0.4, "rgba(200,230,245,0.05)");
      sk.addColorStop(1, "rgba(0,0,0,0)");
      b.fillStyle = sk; b.fill();
      // åkander (3 klynger m. blomst)
      const lily = (lx, ly, s, withFlower) => {
        b.fillStyle = "#3e8a3c";
        b.beginPath(); b.ellipse(lx, ly, 17 * s, 12 * s, 0.3, 0.45, Math.PI * 2.25); b.lineTo(lx, ly); b.closePath(); b.fill();
        b.fillStyle = "rgba(255,255,255,0.12)";
        b.beginPath(); b.ellipse(lx - 3 * s, ly - 3 * s, 8 * s, 5 * s, 0.3, 0, Math.PI * 2); b.fill();
        if (withFlower) {
          for (let p = 0; p < 6; p++) {
            const pa = (p / 6) * Math.PI * 2;
            b.fillStyle = "#f4c8d8";
            b.beginPath(); b.ellipse(lx + 12 * s + Math.cos(pa) * 4, ly - 6 * s + Math.sin(pa) * 3.2, 4, 2.4, pa, 0, Math.PI * 2); b.fill();
          }
          b.fillStyle = "#e8c15a";
          b.beginPath(); b.arc(lx + 12 * s, ly - 6 * s, 2.2, 0, Math.PI * 2); b.fill();
        }
      };
      lily(POND.x + 46, POND.y + 44, 1, true);
      lily(POND.x + POND.w - 52, POND.y + 60, 0.85, false);
      lily(POND.x + 60, POND.y + POND.h - 46, 0.9, true);
      // frøens åkande (mockup: frø på blad nederst til højre)
      const fx = POND.x + POND.w - 74, fy = POND.y + POND.h - 52;
      lily(fx, fy, 1.05, false);
      b.fillStyle = "#6a8f3c";
      b.beginPath(); b.ellipse(fx + 2, fy - 6, 9, 6.5, 0, 0, Math.PI * 2); b.fill();
      b.fillStyle = "#57772f";
      b.beginPath(); b.ellipse(fx - 1, fy - 5, 2.2, 1.5, 0.4, 0, Math.PI * 2); b.fill();
      b.beginPath(); b.ellipse(fx + 5, fy - 4, 1.8, 1.2, -0.3, 0, Math.PI * 2); b.fill();
      // øjne + forben
      for (const ex of [fx - 3, fx + 7]) {
        b.fillStyle = "#7da34a";
        b.beginPath(); b.arc(ex, fy - 12, 2.6, 0, Math.PI * 2); b.fill();
        b.fillStyle = "#1e2410";
        b.beginPath(); b.arc(ex, fy - 12.4, 1.1, 0, Math.PI * 2); b.fill();
      }
      b.strokeStyle = "#57772f"; b.lineWidth = 1.8;
      b.beginPath(); b.moveTo(fx - 5, fy - 2); b.lineTo(fx - 7, fy + 2); b.stroke();
      b.beginPath(); b.moveTo(fx + 9, fy - 2); b.lineTo(fx + 11, fy + 2); b.stroke();
      b.lineWidth = 1;
      // siv m. ægte dunhammere i fire hjørner
      const reeds = (rx, ry, flip) => {
        for (let i = 0; i < 5; i++) {
          const x = rx + i * 6 * flip, sway = (rnd(i * 31) - 0.5) * 10;
          const top = ry - 52 - rnd(i * 7) * 16;
          b.strokeStyle = i % 2 ? "#4a7a35" : "#5d8f42";
          b.lineWidth = 2.4;
          b.beginPath(); b.moveTo(x, ry);
          b.quadraticCurveTo(x + sway, ry - 30, x + sway * 1.4, top);
          b.stroke();
          if (i % 2) {
            // dunhammer: brun cigar + lys spids
            const hx = x + sway * 1.4, hy = top - 2;
            b.fillStyle = "#6b4226";
            b.beginPath(); b.ellipse(hx, hy - 7, 3.4, 9.5, sway * 0.015, 0, Math.PI * 2); b.fill();
            b.fillStyle = "rgba(255,235,200,0.35)";
            b.beginPath(); b.ellipse(hx - 1, hy - 12, 1.2, 4, sway * 0.015, 0, Math.PI * 2); b.fill();
            b.strokeStyle = "#8a9a4a"; b.lineWidth = 1.4;
            b.beginPath(); b.moveTo(hx, hy - 16); b.lineTo(hx + 1, hy - 22); b.stroke();
          }
        }
        b.lineWidth = 1;
      };
      reeds(POND.x + 6, POND.y + 6, 1);
      reeds(POND.x + POND.w - 8, POND.y + 6, -1);
      reeds(POND.x + 4, POND.y + POND.h + 2, 1);
      reeds(POND.x + POND.w - 8, POND.y + POND.h + 4, -1);
      // FLETTET KURV m. 2x5 reder (Gemini-mockup: fiskede ænder ligger i kurven)
      const bw = BASKET;
      rr2(bw.x, bw.y, bw.w, bw.h, 26);
      const bgK = b.createLinearGradient(0, bw.y, 0, bw.y + bw.h);
      bgK.addColorStop(0, "#9a6b3a"); bgK.addColorStop(1, "#6b4426");
      b.fillStyle = bgK; b.fill();
      // fletning: krydsede diagonale bånd
      b.save();
      rr2(bw.x, bw.y, bw.w, bw.h, 26);
      b.clip();
      for (let i = -8; i < 30; i++) {
        b.strokeStyle = i % 2 ? "rgba(60,32,12,0.35)" : "rgba(255,225,170,0.14)";
        b.lineWidth = 5;
        b.beginPath(); b.moveTo(bw.x + i * 16, bw.y); b.lineTo(bw.x + i * 16 + 26, bw.y + bw.h); b.stroke();
        b.beginPath(); b.moveTo(bw.x + i * 16 + 26, bw.y); b.lineTo(bw.x + i * 16, bw.y + bw.h); b.stroke();
      }
      b.restore();
      // reb-kant (to snoede linjer)
      rr2(bw.x + 2, bw.y + 2, bw.w - 4, bw.h - 4, 24);
      b.strokeStyle = "#c9a05c"; b.lineWidth = 5; b.stroke();
      rr2(bw.x + 2, bw.y + 2, bw.w - 4, bw.h - 4, 24);
      b.strokeStyle = "rgba(80,45,15,0.5)"; b.lineWidth = 1.6;
      b.setLineDash([5, 4]); b.stroke(); b.setLineDash([]);
      // 10 reder (flettede ringe m. mos)
      for (let i = 0; i < 10; i++) {
        const s = slotPos(i);
        b.fillStyle = "rgba(40,22,8,0.45)";
        b.beginPath(); b.ellipse(s.x, s.y + 3, 25, 15, 0, 0, Math.PI * 2); b.fill();
        b.fillStyle = "#4f6a30";
        b.beginPath(); b.ellipse(s.x, s.y + 1, 22, 12.5, 0, 0, Math.PI * 2); b.fill();
        b.strokeStyle = "#8a5f36"; b.lineWidth = 4;
        b.beginPath(); b.ellipse(s.x, s.y, 23, 13, 0, 0, Math.PI * 2); b.stroke();
        b.strokeStyle = "rgba(255,225,170,0.35)"; b.lineWidth = 1.4;
        b.setLineDash([4, 3]);
        b.beginPath(); b.ellipse(s.x, s.y, 23, 13, 0, 0, Math.PI * 2); b.stroke();
        b.setLineDash([]);
        // mos-totter i reden
        for (let k = 0; k < 4; k++) {
          b.strokeStyle = "rgba(140,170,80,0.5)"; b.lineWidth = 1.2;
          const ax = s.x - 14 + rnd(i * 9 + k) * 28, ay = s.y - 2 + rnd(i * 5 + k) * 7;
          b.beginPath(); b.moveTo(ax, ay); b.lineTo(ax + 4, ay - 3); b.stroke();
        }
      }
      b.lineWidth = 1;
      // vignette
      const vg = b.createRadialGradient(W / 2, H * 0.45, 140, W / 2, H * 0.5, 430);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(10,10,6,0.4)");
      b.fillStyle = vg; b.fillRect(0, 0, W, H);
    }

    /* ---- input ---- */
    const inPond = (x, y) => {
      const dx = (x - PC.x) / (POND.w / 2 - 14), dy = (y - PC.y) / (POND.h / 2 - 14);
      return dx * dx + dy * dy <= 1;
    };
    const toXY = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: ((p.clientX - r.left) / r.width) * W, y: ((p.clientY - r.top) / r.height) * H };
    };
    const move = (e) => {
      const p = toXY(e);
      if (inPond(p.x, p.y)) g.aim = p;
    };
    const beginCatch = (p, forceIdx) => {
      g.state = "lower";
      g.stT = 0;
      g.aim = p;
      g.forceIdx = forceIdx;
    };
    const press = (e) => {
      audioRef.current.unlock();
      e.preventDefault();
      if (g.over || g.state !== "idle") return;
      const p = toXY(e);
      if (!inPond(p.x, p.y)) return;
      beginCatch(p);
    };
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("touchmove", move, { passive: true });
    canvas.addEventListener("mousedown", press);
    canvas.addEventListener("touchstart", press, { passive: false });

    const finish = () => {
      if (g.over) return;
      g.over = true;
      const counts = { gron: 0, blaa: 0, guld: 0, joker: 0 };
      g.caught.forEach((c) => counts[c.mark]++);
      const prizes = [];
      for (let i = 0; i < Math.floor(counts.guld / 3); i++) prizes.push("stor");
      for (let i = 0; i < Math.floor(counts.blaa / 3); i++) prizes.push("mellem");
      for (let i = 0; i < Math.floor(counts.gron / 3); i++) prizes.push("lille");
      if (prizes.length) audioRef.current.fanfare();
      setTimeout(() => {
        setResult({ counts, prizes });
        // gevinst -> DIREKTE til bamsevalget (kan ikke springes over);
        // opsummeringskortet vises først, når alle bamser er valgt
        if (prizes.length) setPickQueue(prizes);
        setPhase("result");
      }, 650);
    };

    /* ---- step ---- */
    const step = (dt) => {
      g.t += dt;
      g.stT += dt;
      for (const d of g.ducks) if (d.state === "swim") d.th += d.sp * dt;

      // spontane ringe i vandet
      g.rippleT -= dt;
      if (g.rippleT <= 0) {
        g.rippleT = 0.5 + Math.random() * 0.9;
        const a = Math.random() * Math.PI * 2, rr = Math.random();
        g.ripples.push({
          x: PC.x + Math.cos(a) * rr * (POND.w / 2 - 40),
          y: PC.y + Math.sin(a) * rr * (POND.h / 2 - 40),
          t: 0, max: 10 + Math.random() * 14,
        });
      }
      g.ripples.forEach((r) => (r.t += dt));
      g.ripples = g.ripples.filter((r) => r.t < 1.4);
      g.drops.forEach((p) => { p.t += dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 500 * dt; });
      g.drops = g.drops.filter((p) => p.t < 0.6);
      if (g.popup) { g.popup.t += dt; if (g.popup.t > 1.4) g.popup = null; }

      if (g.state === "lower" && g.stT > 0.24) {
        // magneten er nede: fang nærmeste svømmende and inden for radius
        let bi = -1, bd = 1e9;
        if (g.forceIdx !== undefined && g.ducks[g.forceIdx]?.state === "swim") { bi = g.forceIdx; bd = 0; }
        else {
          g.ducks.forEach((d, i) => {
            if (d.state !== "swim") return;
            const p = duckPos(d);
            const dd = Math.hypot(p.x - g.aim.x, p.y - g.aim.y);
            if (dd < bd) { bd = dd; bi = i; }
          });
        }
        g.forceIdx = undefined;
        g.ripples.push({ x: g.aim.x, y: g.aim.y, t: 0, max: 18 });
        if (bi >= 0 && bd <= GRAB_R) {
          g.target = bi;
          g.ducks[bi].state = "hooked";
          const p = duckPos(g.ducks[bi]);
          g.hook = { x: p.x, y: p.y };
          g.state = "lift";
          audioRef.current.snap();
          audioRef.current.reel();
          for (let i = 0; i < 7; i++) {
            g.drops.push({ x: p.x, y: p.y, vx: (Math.random() - 0.5) * 120, vy: -100 - Math.random() * 120, t: 0 });
          }
        } else {
          g.state = "miss";
          audioRef.current.splash();
        }
        g.stT = 0;
      }
      if (g.state === "miss" && g.stT > 0.3) { g.state = "idle"; g.stT = 0; }
      if (g.state === "lift" && g.stT > 0.5) {
        g.state = "reveal";
        g.stT = 0;
        const mark = g.ducks[g.target].mark;
        audioRef.current.reveal(mark);
        g.popup = {
          txt: mark === "joker" ? t("dp_joker") : t("dp_found_" + mark),
          color: MARK_COLOR[mark], t: 0,
        };
      }
      if (g.state === "reveal" && g.stT > 1.05) {
        g.state = "rack"; g.stT = 0;
      }
      if (g.state === "rack" && g.stT > 0.3) {
        const d = g.ducks[g.target];
        d.state = "racked";
        g.caught.push({ mark: d.mark });
        setHud({ fished: g.caught.length, marks: g.caught.map((c) => c.mark) });
        g.target = null;
        g.state = "idle"; g.stT = 0;
        if (g.caught.length >= CATCHES) finish();
      }
    };

    /* ---- tegning ---- */
    const drawDuck = (x, y, dir, rot, scale, showBelly, mark, wake) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.scale(scale * dir, scale);
      if (wake) {
        ctx.strokeStyle = "rgba(255,255,255,0.22)";
        ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(-14, 6, 9, -0.6, 0.9); ctx.stroke();
        ctx.beginPath(); ctx.arc(-19, 7, 13, -0.4, 0.7); ctx.stroke();
        ctx.lineWidth = 1;
      }
      if (showBelly) {
        // vendt om: bunden m. mærket
        const bg2 = ctx.createRadialGradient(0, 0, 3, 0, 0, 18);
        bg2.addColorStop(0, "#ffdf6e"); bg2.addColorStop(1, "#e8b93a");
        ctx.fillStyle = bg2;
        ctx.beginPath(); ctx.ellipse(0, 0, 17, 13, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(120,80,10,0.5)"; ctx.stroke();
        if (mark === "guld") {
          ctx.fillStyle = "#c9982e";
          ctx.beginPath(); ctx.arc(0, 0, 8.5, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "#f2d67e";
          ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill();
          // teddy-hoved i relief
          ctx.fillStyle = "#c9982e";
          ctx.beginPath(); ctx.arc(0, 0.5, 3.4, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(-2.6, -2.6, 1.5, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(2.6, -2.6, 1.5, 0, Math.PI * 2); ctx.fill();
        } else if (mark === "joker") {
          ctx.fillStyle = MARK_COLOR.joker;
          ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = "#fff"; ctx.lineWidth = 2.4;
          ctx.beginPath();
          ctx.moveTo(-3.6, -3.6); ctx.lineTo(3.6, 3.6);
          ctx.moveTo(3.6, -3.6); ctx.lineTo(-3.6, 3.6);
          ctx.stroke(); ctx.lineWidth = 1;
        } else {
          ctx.fillStyle = MARK_COLOR[mark];
          ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "rgba(255,255,255,0.35)";
          ctx.beginPath(); ctx.arc(-2, -2.4, 3, 0, Math.PI * 2); ctx.fill();
        }
      } else {
        // krop
        const bodyG = ctx.createLinearGradient(0, -14, 0, 10);
        bodyG.addColorStop(0, "#ffd94f");
        bodyG.addColorStop(1, "#eab225");
        ctx.fillStyle = bodyG;
        ctx.beginPath(); ctx.ellipse(0, 0, 16, 11, 0, 0, Math.PI * 2); ctx.fill();
        // hale-vip
        ctx.beginPath(); ctx.moveTo(-13, -3); ctx.quadraticCurveTo(-21, -9, -15, -11);
        ctx.quadraticCurveTo(-12, -7, -10, -6); ctx.closePath(); ctx.fill();
        // vinge
        ctx.strokeStyle = "rgba(160,110,20,0.55)"; ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.ellipse(-2, 1, 7, 4.5, -0.2, 0.4, Math.PI * 1.4); ctx.stroke();
        ctx.lineWidth = 1;
        // hoved
        const hg = ctx.createRadialGradient(8, -13, 2, 9, -11, 10);
        hg.addColorStop(0, "#ffe27a"); hg.addColorStop(1, "#f0bf35");
        ctx.fillStyle = hg;
        ctx.beginPath(); ctx.arc(9, -11, 8.4, 0, Math.PI * 2); ctx.fill();
        // næb
        ctx.fillStyle = "#f08b1f";
        ctx.beginPath();
        ctx.moveTo(16, -12.5); ctx.quadraticCurveTo(24, -11.5, 16.5, -8.6);
        ctx.quadraticCurveTo(15.5, -10.4, 16, -12.5); ctx.closePath(); ctx.fill();
        // øje
        ctx.fillStyle = "#2b1d0a";
        ctx.beginPath(); ctx.arc(11, -13, 1.7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.beginPath(); ctx.arc(11.6, -13.6, 0.6, 0, Math.PI * 2); ctx.fill();
        // magnetplade på ryggen
        ctx.fillStyle = "#9aa4ae";
        ctx.beginPath(); ctx.ellipse(-3, -8.6, 4.4, 2.1, -0.25, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.beginPath(); ctx.ellipse(-4, -9.2, 1.8, 0.8, -0.25, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
    };

    const drawRod = () => {
      if (g.over) return;
      const lower = g.state === "lower" || g.state === "miss";
      const lift = g.state === "lift" || g.state === "reveal" || g.state === "rack";
      let mx = g.aim.x, my = g.aim.y;
      let ext = -14;                       // magnetens højde over vandet
      if (lower) ext = Math.min(g.stT / 0.24, 1) * 14 - 14 + (g.state === "miss" ? -g.stT * 40 : 0);
      if (lift) { mx = g.hook.x; my = g.hook.y; }
      const liftK = g.state === "lift" ? Math.min(g.stT / 0.5, 1) : (g.state === "reveal" || g.state === "rack" ? 1 : 0);
      const magY = lift ? my - liftK * 90 : my + ext;
      const tip = { x: mx + 26, y: Math.min(magY - 60, POND.y - 8) };
      // stang fra nederste højre hjørne
      ctx.strokeStyle = "#6b4a2a"; ctx.lineWidth = 5; ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(W - 6, H - 60);
      ctx.quadraticCurveTo(W - 60, tip.y + 120, tip.x, tip.y);
      ctx.stroke();
      ctx.strokeStyle = "#8a5f36"; ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(W - 8, H - 64);
      ctx.quadraticCurveTo(W - 62, tip.y + 118, tip.x, tip.y);
      ctx.stroke();
      // line + magnet (hestesko)
      ctx.strokeStyle = "rgba(240,238,230,0.8)"; ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.moveTo(tip.x, tip.y); ctx.lineTo(mx, magY - 8); ctx.stroke();
      ctx.lineWidth = 4.6; ctx.lineCap = "round";
      ctx.strokeStyle = "#c8352e";
      ctx.beginPath(); ctx.arc(mx, magY, 6, Math.PI * 0.15, Math.PI * 0.85, true); ctx.stroke();
      ctx.strokeStyle = "#e8ecf0"; ctx.lineWidth = 2.2;
      ctx.beginPath(); ctx.moveTo(mx - 5.4, magY + 2.4); ctx.lineTo(mx - 5.4, magY + 5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(mx + 5.4, magY + 2.4); ctx.lineTo(mx + 5.4, magY + 5); ctx.stroke();
      ctx.lineWidth = 1;
      // magnet-glød på vandet (mockuppets lysende ring)
      if (g.state === "idle" || g.state === "lower") {
        const gl = ctx.createRadialGradient(mx, my + 2, 2, mx, my + 2, 22);
        gl.addColorStop(0, "rgba(140,220,240,0.32)");
        gl.addColorStop(1, "rgba(140,220,240,0)");
        ctx.fillStyle = gl;
        ctx.beginPath(); ctx.ellipse(mx, my + 2, 22, 14, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(150,225,245,0.55)";
        ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.ellipse(mx, my + 2, 13, 7.5, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.lineWidth = 1;
      }
      if (g.state === "idle") {
        ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.ellipse(mx, my, GRAB_R * 0.7, GRAB_R * 0.45, 0, 0, Math.PI * 2); ctx.stroke();
        ctx.setLineDash([]);
      }
    };

    const draw = () => {
      ctx.drawImage(bg, 0, 0, W, H);
      // levende vand: drivende lysstriber
      ctx.save();
      ctx.beginPath();
      ctx.roundRect ? ctx.roundRect(POND.x, POND.y, POND.w, POND.h, POND.r) : ctx.rect(POND.x, POND.y, POND.w, POND.h);
      ctx.clip();
      for (let i = 0; i < 4; i++) {
        const yy = POND.y + 50 + i * 66 + Math.sin(g.t * 0.7 + i * 2) * 8;
        ctx.strokeStyle = `rgba(255,255,255,${0.05 + (i % 2) * 0.03})`;
        ctx.lineWidth = 5;
        ctx.beginPath();
        for (let x = POND.x; x <= POND.x + POND.w; x += 12) {
          const y2 = yy + Math.sin(x * 0.05 + g.t * 1.3 + i) * 3.5;
          x === POND.x ? ctx.moveTo(x, y2) : ctx.lineTo(x, y2);
        }
        ctx.stroke();
      }
      ctx.lineWidth = 1;
      // ringe
      for (const r of g.ripples) {
        const k = r.t / 1.4;
        ctx.strokeStyle = `rgba(255,255,255,${0.3 * (1 - k)})`;
        ctx.beginPath(); ctx.ellipse(r.x, r.y, r.max * k + 3, (r.max * k + 3) * 0.55, 0, 0, Math.PI * 2); ctx.stroke();
      }
      // ænder (svømmende) m. refleks
      g.ducks.forEach((d, i) => {
        if (d.state !== "swim") return;
        const p = duckPos(d);
        const dir = Math.cos(d.th + (d.sp > 0 ? Math.PI / 2 : -Math.PI / 2)) >= 0 ? 1 : -1;
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.translate(p.x, p.y + 14);
        ctx.scale(dir, -0.5);
        ctx.fillStyle = "#a97b12";
        ctx.beginPath(); ctx.ellipse(0, 0, 15, 10, 0, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        drawDuck(p.x, p.y, dir, Math.sin(g.t * 2 + d.bob) * 0.07, 1, false, null, true);
      });
      ctx.restore();   // clip slut

      // fanget and (over vandet, uden clip)
      if (g.target !== null && g.target !== undefined && g.ducks[g.target]) {
        const d = g.ducks[g.target];
        if (g.state === "lift") {
          const k = Math.min(g.stT / 0.5, 1);
          drawDuck(g.hook.x, g.hook.y - k * 90, 1, Math.sin(g.stT * 14) * 0.1 * (1 - k), 1 + k * 0.5, false, null, false);
        } else if (g.state === "reveal" || g.state === "rack") {
          // vendes: skala-x klemmes gennem 0 og viser bunden
          const k = Math.min(g.stT / 0.28, 1);
          const flip = g.state === "rack" ? 1 : k;
          let x = g.hook.x, y = g.hook.y - 90;
          if (g.state === "rack") {
            const rk = Math.min(g.stT / 0.3, 1);
            const slot = slotPos(g.caught.length);
            x += (slot.x - x) * rk;
            y += (slot.y - y) * rk;
          }
          const sc = g.state === "rack" ? 1.5 - Math.min(g.stT / 0.3, 1) * 0.75 : 1.5;
          drawDuck(x, y, 1, 0, sc * (0.2 + 0.8 * Math.abs(flip * 2 - 1)), flip > 0.5, d.mark, false);
        }
      }
      // dråber
      for (const p of g.drops) {
        ctx.globalAlpha = Math.max(0, 1 - p.t / 0.6);
        ctx.fillStyle = "#bfe4f2";
        ctx.beginPath(); ctx.arc(p.x, p.y, 2, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      }
      drawRod();
      // guldsmede over dammen (to stk. på dovne sinus-baner)
      for (let i = 0; i < 2; i++) {
        const dxp = PC.x + Math.cos(g.t * 0.26 + i * 2.8) * (110 - i * 30);
        const dyp = POND.y + 62 + i * 42 + Math.sin(g.t * 0.9 + i * 1.7) * 24;
        const ang = Math.atan2(
          Math.cos(g.t * 0.9 + i * 1.7) * 24 * 0.9,
          -Math.sin(g.t * 0.26 + i * 2.8) * (110 - i * 30) * 0.26
        );
        ctx.save();
        ctx.translate(dxp, dyp);
        ctx.rotate(ang);
        const flut = 0.35 + Math.abs(Math.sin(g.t * 26 + i)) * 0.65;
        ctx.globalAlpha = 0.85;
        ctx.strokeStyle = i ? "#3e8a9a" : "#7a4a9a";
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(-7, 0); ctx.lineTo(6, 0); ctx.stroke();
        ctx.fillStyle = i ? "#3e8a9a" : "#7a4a9a";
        ctx.beginPath(); ctx.arc(6.5, 0, 1.8, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(210,235,245,${0.5 * flut})`;
        for (const s of [-1, 1]) {
          ctx.beginPath(); ctx.ellipse(0, s * 4 * flut, 6.5, 2.4, s * 0.5, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.ellipse(2.5, s * 3.4 * flut, 5, 2, s * 0.6, 0, Math.PI * 2); ctx.fill();
        }
        ctx.restore();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      // fiskede ænder i kurvens reder m. deres mærke
      g.caught.forEach((c, i) => {
        const s = slotPos(i);
        drawDuck(s.x, s.y - 3, 1, 0, 0.8, true, c.mark, false);
      });
      // popup
      if (g.popup) {
        const k = g.popup.t;
        ctx.globalAlpha = Math.min(1, 3 - k * 2.2);
        ctx.font = "bold 24px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "rgba(10,20,16,0.8)";
        ctx.strokeText(g.popup.txt, W / 2, 84 - k * 14);
        ctx.fillStyle = g.popup.color;
        ctx.fillText(g.popup.txt, W / 2, 84 - k * 14);
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
      }
      // hint (over kurvens kant som i mockuppet)
      if (g.caught.length === 0 && g.state === "idle") {
        ctx.font = "600 13px Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 3; ctx.strokeStyle = "rgba(30,16,4,0.8)";
        ctx.strokeText(t("dp_hint"), W / 2, BASKET.y - 6, W - 40);
        ctx.fillStyle = "#f2dfae";
        ctx.fillText(t("dp_hint"), W / 2, BASKET.y - 6, W - 40);
        ctx.lineWidth = 1;
      }
    };

    let raf, last = performance.now();
    const loop = (ts) => {
      const dt = Math.min((ts - last) / 1000, 0.033);
      last = ts;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("mousedown", press);
      canvas.removeEventListener("touchstart", press);
    };
  }, [phase]);

  /* ================= UI ================= */
  const markChip = (mark, n) => (
    <span key={mark} className={"dp-chip m-" + mark}>
      <i style={{ background: MARK_COLOR[mark] }} />{n}
    </span>
  );

  return (
    <div className={"dp-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <h1>{t("tile_duck").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("dp_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("dp_lede")}</p>
          <ul className="rules">
            <li>{t("dp_rule1")}</li>
            <li>{t("dp_rule2")}</li>
            <li>{t("dp_rule3")}</li>
          </ul>
          <button className="cta" onClick={startGame}>{t("dp_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("dp_fished", { n: hud.fished, m: CATCHES })}</span>
            <span className="dp-chips">
              {["gron", "blaa", "guld", "joker"].map((m) =>
                markChip(m, hud.marks.filter((x) => x === m).length))}
            </span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "result" && result && !pickQueue && (
        <div className="overlay">
          <div className="card">
            <div className={"ribbon" + (result.prizes.length ? "" : " dark")}>
              {result.prizes.length === 0 ? t("dp_res_none")
                : result.prizes.length === 1 ? t("dp_res_win1")
                : t("dp_res_win", { n: result.prizes.length })}
            </div>
            <div className="dp-break">
              {["gron", "blaa", "guld", "joker"].map((m) => (
                <div key={m} className="dp-row">
                  <span><i className="dp-dot" style={{ background: MARK_COLOR[m] }} />
                    {m === "joker" ? t("dp_joker") : t("dp_found_" + m)}</span>
                  <b>{result.counts[m]}{m !== "joker" && Math.floor(result.counts[m] / 3) > 0 ? " ★" : ""}</b>
                </div>
              ))}
            </div>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {pickQueue && pickQueue.length > 0 && (
        <PrizePicker
          size={pickQueue[0]}
          game={t("tile_duck")}
          onDone={() => {
            const rest = pickQueue.slice(1);
            // sidste valg: luk kun køen — result/ribbon består til opsummeringen
            setPickQueue(rest.length ? rest : null);
          }}
        />
      )}
    </div>
  );
}

const css = `
.dp-root{
  --cream:#F6EED9; --ink:#22301c; --red:#C8352E;
  min-height:100vh; background:#12251c;
  background-image:radial-gradient(circle at 50% -10%, #2c5140 0%, #12251c 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #1e4534, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.cta{
  width:100%; margin-top:14px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.gold{
  background:linear-gradient(180deg,#ffd75e,#f0a92e); color:#4a3305; box-shadow:0 4px 0 #b07a14;
  font-size:16px; padding:12px;
}
.cta.gold:active{box-shadow:0 1px 0 #b07a14;}
.cta.alt{margin-top:10px; background:#4a6a58; box-shadow:0 4px 0 #2c4536; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #2c4536;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.dp-chips{display:flex; gap:6px;}
.dp-chip{
  display:inline-flex; align-items:center; gap:4px;
  background:rgba(255,255,255,.12); padding:2px 8px; border-radius:20px; font-size:13px;
}
.dp-chip i{width:10px; height:10px; border-radius:50%; display:inline-block;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #35543f; box-shadow:0 12px 30px rgba(0,0,0,.55);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(8,16,12,.78); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:20px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.dark{background:#4a6a58;}
.dp-break{margin:0 0 8px;}
.dp-row{
  display:flex; justify-content:space-between; align-items:center; font-weight:800;
  font-size:14px; padding:6px 2px; border-bottom:1px dashed #cbbf9c;
}
.dp-row:last-child{border-bottom:none;}
.dp-dot{width:12px; height:12px; border-radius:50%; display:inline-block; margin-right:8px; vertical-align:-1px;}
`;
